import React, { useEffect, useState, useCallback } from 'react';
import authFetch from '../utils/authFetch';
import { runPlanRebuild } from '../utils/planRebuild';
import AIInsightsCard from '../components/AIInsightsCard';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

const REC_COLORS = {
  last_workout:       { btn: 'bg-indigo-500 hover:bg-indigo-600', badge: 'text-indigo-300 bg-indigo-500/10 border-indigo-500/30' },
  rest_day:           { btn: 'bg-red-500 hover:bg-red-600',      badge: 'text-red-400 bg-red-500/10 border-red-500/30' },
  pace_update:        { btn: 'bg-blue-500 hover:bg-blue-600',    badge: 'text-blue-400 bg-blue-500/10 border-blue-500/30' },
  increase_volume:    { btn: 'bg-amber-500 hover:bg-amber-600',  badge: 'text-amber-400 bg-amber-500/10 border-amber-500/30' },
  reduce_load:        { btn: 'bg-orange-500 hover:bg-orange-600',badge: 'text-orange-400 bg-orange-500/10 border-orange-500/30' },
  fatigue_warning:    { btn: 'bg-red-600 hover:bg-red-700',      badge: 'text-red-300 bg-red-600/10 border-red-500/30' },
  hr_observation:     { btn: 'bg-pink-500 hover:bg-pink-600',    badge: 'text-pink-400 bg-pink-500/10 border-pink-500/30' },
  next_workout_focus: { btn: 'bg-purple-500 hover:bg-purple-600',badge: 'text-purple-400 bg-purple-500/10 border-purple-500/30' },
  training_load:      { btn: 'bg-teal-500 hover:bg-teal-600',    badge: 'text-teal-400 bg-teal-500/10 border-teal-500/30' },
  plan_deviation:     { btn: 'bg-yellow-500 hover:bg-yellow-600',badge: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' },
};

const REC_ICONS = {
  last_workout:       '🏃',
  rest_day:           '😴',
  pace_update:        '⚡',
  increase_volume:    '📈',
  reduce_load:        '🔽',
  fatigue_warning:    '🚨',
  hr_observation:     '❤️',
  next_workout_focus: '🎯',
  training_load:      '📊',
  plan_deviation:     '⚠️',
};

export { REC_COLORS, REC_ICONS };
function ScoreBar({ score }) {
  const color = score >= 80 ? 'bg-emerald-400' : score >= 60 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="mt-2">
      <div className="flex justify-between text-[10px] text-gray-500 mb-1">
        <span>ציון אימון</span><span className="text-white font-semibold">{score}/100</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

function WorkoutPhaseRow({ lw }) {
  if (!lw) return null;
  const wa = lw.workout_analysis || {};
  const hasPhases = wa.warmup_km || wa.tempo_km || wa.cooldown_km;
  if (!hasPhases) return null;
  return (
    <div className="flex gap-1 mt-3 text-center text-[11px]">
      {wa.warmup_km > 0 && (
        <div className="flex-1 bg-white/5 rounded-lg px-1 py-2">
          <div className="text-gray-400 font-bold">{wa.warmup_km} ק"מ</div>
          {wa.warmup_pace && <div className="text-gray-500 mt-0.5">{wa.warmup_pace}</div>}
          <div className="text-gray-600 text-[10px] mt-0.5">חימום</div>
        </div>
      )}
      {wa.tempo_km > 0 && (
        <div className="flex-1 bg-blue-500/10 border border-blue-500/20 rounded-lg px-1 py-2">
          <div className="text-blue-300 font-bold">{wa.tempo_km} ק"מ</div>
          {wa.tempo_pace && <div className="text-blue-400 mt-0.5">{wa.tempo_pace}</div>}
          <div className="text-blue-500/60 text-[10px] mt-0.5">מרכזי</div>
        </div>
      )}
      {wa.cooldown_km > 0 && (
        <div className="flex-1 bg-white/5 rounded-lg px-1 py-2">
          <div className="text-gray-400 font-bold">{wa.cooldown_km} ק"מ</div>
          {wa.cooldown_pace && <div className="text-gray-500 mt-0.5">{wa.cooldown_pace}</div>}
          <div className="text-gray-600 text-[10px] mt-0.5">שחרור</div>
        </div>
      )}
    </div>
  );
}

export function ReinforcementCard({ data, lastWorkout }) {
  if (!data) return null;
  const { greeting, reason, stats } = data;
  const score = lastWorkout?.overall_score;
  const avgHr = lastWorkout?.avg_hr || stats?.avg_hr;
  const maxHr = lastWorkout?.max_hr;
  const hrPct = lastWorkout?.avg_hr_pct || stats?.avg_hr_pct;
  return (
    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 mb-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="text-2xl mt-0.5">🏆</div>
        <div className="flex-1 min-w-0">
          <h3 className="text-emerald-300 font-bold text-base leading-snug">{greeting}</h3>
          {reason && (
            <p className="text-emerald-200/70 text-sm mt-1 leading-relaxed">{reason}</p>
          )}
        </div>
      </div>

      {/* Phase breakdown (if last workout available) */}
      <WorkoutPhaseRow lw={lastWorkout} />

      {/* HR row */}
      {avgHr && (
        <div className="flex items-center gap-2 mt-3 bg-pink-500/10 border border-pink-500/20 rounded-xl px-3 py-2">
          <span className="text-base">❤️</span>
          <span className="text-pink-300 font-bold text-sm">{avgHr} BPM</span>
          {maxHr && <span className="text-pink-500/60 text-xs">/ {maxHr}</span>}
          {hrPct && <span className="text-pink-400/70 text-xs mr-auto">({hrPct}% מקסימום)</span>}
        </div>
      )}

      {/* Score bar */}
      {score != null && <ScoreBar score={score} />}

      {/* Weekly stats row */}
      {stats && (
        <div className="flex gap-2 mt-3 justify-center">
          {stats.completed && (
            <div className="bg-emerald-500/10 rounded-xl p-2.5 text-center flex-1">
              <div className="text-white font-bold text-base">{stats.completed}</div>
              <div className="text-emerald-300/60 text-[11px] mt-0.5">אימונים שבועי</div>
            </div>
          )}
          {stats.race_pace && (
            <div className="bg-blue-500/10 rounded-xl p-2.5 text-center flex-1">
              <div className="text-blue-300 font-bold text-base">{stats.race_pace}</div>
              <div className="text-blue-300/60 text-[11px] mt-0.5">קצב מרכזי</div>
            </div>
          )}
          {stats.streak_weeks != null && (
            <div className="bg-emerald-500/10 rounded-xl p-2.5 text-center flex-1">
              <div className="text-white font-bold text-base">{stats.streak_weeks}</div>
              <div className="text-emerald-300/60 text-[11px] mt-0.5">שובע נוכחי</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function FatigueBar({ pct }) {
  if (!pct) return null;
  const color = pct >= 80 ? 'bg-red-500' : pct >= 60 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="mt-2">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-500">מד עייפות</span>
        <span className={pct >= 80 ? 'text-red-400' : pct >= 60 ? 'text-amber-400' : 'text-emerald-400'}>{pct}%</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function RecommendationCard({ rec, userId, onDismiss }) {
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [expanded, setExpanded] = useState(false);

  const colors = REC_COLORS[rec.type] || REC_COLORS.pace_update;
  const icon = REC_ICONS[rec.type] || '💡';
  const { from, to } = rec.change_tag || {};

  // Only reduce_load (fatigue-driven volume cut) and add_rest trigger action buttons.
  // increase_volume / update_race_pace are disabled until we add proper confirmation UX.
  const isActionable = (
    (rec.action?.type === 'reduce_load' && rec.action?.new_km) ||
    (rec.action?.type === 'add_rest' && rec.action?.day_name)
  );

  // Split reason into first sentence (preview) + the rest (expandable)
  const reasonFull = Array.isArray(rec.reason) ? rec.reason.join(' ') : (rec.reason || '');
  const firstDot = reasonFull.search(/\.(?:\s|$)/);
  const preview = firstDot > 0 ? reasonFull.slice(0, firstDot + 1) : reasonFull.slice(0, 100);
  const rest = firstDot > 0 ? reasonFull.slice(firstDot + 1).trim() : reasonFull.slice(100).trim();
  const hasMore = rest.length > 0;

  const handleAction = async () => {
    if (!isActionable) { onDismiss(rec.type); return; }
    setStatus('loading');
    try {
      if (rec.action?.type === 'add_rest' && rec.action?.day_name) {
        await authFetch(`${API_BASE_URL}/weekly-plan/set-rest-day?user_id=${userId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ day_name: rec.action.day_name }),
        });
      } else if (rec.action?.type === 'increase_volume' && rec.action?.new_km) {
        await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ avg_weekly_km: rec.action.new_km }),
        });
        // a plan input changed: the server's rebuild (four weeks from MASTER), not a regenerate that keeps edits
        runPlanRebuild(userId, { reason: 'settings' }).catch(() => {});
      } else if (rec.action?.type === 'update_race_pace' && rec.action?.new_time) {
        await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ goal_time: rec.action.new_time }),
        });
        // a plan input changed: the server's rebuild (four weeks from MASTER), not a regenerate that keeps edits
        runPlanRebuild(userId, { reason: 'settings' }).catch(() => {});
      } else {
        await new Promise(r => setTimeout(r, 300));
      }
      setStatus('done');
      setTimeout(() => onDismiss(rec.type), 2000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2000);
    }
  };

  return (
    <div className={`bg-[#1e293b] border border-white/10 rounded-2xl p-4 mb-3 transition-opacity ${status === 'done' ? 'opacity-40' : ''}`}>

      {/* ── 1. Title row ─────────────────────────── */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg shrink-0">{icon}</span>
        <span className="font-semibold text-white text-sm leading-snug flex-1">{rec.title}</span>
      </div>

      {/* ── 2. Data capsule (from → to) ──────────── */}
      {from && to && (
        <div className="flex items-center gap-1 mb-3 bg-white/5 border border-white/8 rounded-xl px-3 py-2.5">
          <span className={`text-sm font-bold font-mono flex-1 text-center ${colors.badge.split(' ')[0]}`}>{from}</span>
          <span className="text-gray-500 text-base px-1">→</span>
          <span className="text-sm font-bold font-mono text-emerald-300 flex-1 text-center">{to}</span>
        </div>
      )}

      {/* ── 3. Coach note (collapsed → expanded) ─── */}
      <div className="text-xs text-gray-400 leading-relaxed">
        <span>{expanded ? reasonFull : preview}</span>
        {hasMore && !expanded && (
          <button
            onClick={() => setExpanded(true)}
            className="text-blue-400 text-xs underline underline-offset-2 mr-1 hover:text-blue-300 transition-colors"
          >
            קרא עוד
          </button>
        )}
        {expanded && (
          <button
            onClick={() => setExpanded(false)}
            className="text-gray-500 text-xs underline underline-offset-2 mr-1 hover:text-gray-400 transition-colors"
          >
            {' '}הסתר
          </button>
        )}
      </div>

      {rec.fatigue_pct && <FatigueBar pct={rec.fatigue_pct} />}

      {/* ── Action buttons ────────────────────────── */}
      <div className="flex gap-2 mt-3">
        {status === 'done' ? (
          <span className="text-emerald-400 text-sm font-semibold">✅ בוצע</span>
        ) : (
          <>
            <button
              onClick={handleAction}
              disabled={status === 'loading'}
              className={`flex-1 ${colors.btn} text-white text-xs font-semibold py-2 px-3 rounded-xl transition-all disabled:opacity-60`}
            >
              {status === 'loading' ? (
                <span className="flex items-center justify-center gap-1">
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  מעדכן...
                </span>
              ) : status === 'error' ? 'שגיאה — נסה שוב' : !isActionable ? 'הבנתי' :
                rec.action?.type === 'increase_volume' && rec.action?.new_km
                  ? `עדכן ל-${rec.action.new_km} ק"מ/שבוע`
                  : rec.action?.type === 'add_rest' && rec.action?.day_name
                  ? `הוסף מנוחה — ${rec.action.day_name}`
                  : 'עדכן עכשיו'}
            </button>
            <button
              onClick={() => onDismiss(rec.type)}
              className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-500 text-xs hover:bg-white/10 transition-all"
            >
              לא עכשיו
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Home({ userId, userName, onBack, isAdmin = false }) {
  const [insights, setInsights] = useState(null);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | generating | error | no_activities
  const [dismissed, setDismissed] = useState([]);

  const loadInsights = useCallback(async () => {
    let hasExisting = false;
    try {
      const res = await authFetch(`${API_BASE_URL}/insights?user_id=${userId}`);
      const data = await res.json();
      if (data.insights) {
        setInsights(data.insights);
        hasExisting = true;
        setLoadState('ready');
      }
    } catch {}

    // Only generate on first use (no insights yet).
    // After that, the webhook triggers regeneration automatically after new workouts.
    if (!hasExisting) {
      try {
        setLoadState('generating');
        const genRes = await authFetch(`${API_BASE_URL}/insights/generate?user_id=${userId}`, { method: 'POST' });
        const genData = await genRes.json();
        if (genData.success) {
          setInsights({
            reinforcement:     genData.reinforcement,
            recommendations:   genData.recommendations,
            indicators:        genData.indicators,
            last_workout:      genData.last_workout,
            trend_insight_card: genData.trend_insight_card || null,
          });
          setLoadState('ready');
        } else {
          setLoadState('error');
        }
      } catch {
        setLoadState('error');
      }
    }
  }, [userId]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  const handleDismiss = async (type) => {
    setDismissed(prev => [...prev, type]);
    try {
      await authFetch(`${API_BASE_URL}/insights/dismiss?user_id=${userId}&insight_type=${type}`, {
        method: 'POST',
      });
    } catch { /* non-critical */ }
  };

  const handleRefresh = async () => {
    setLoadState('generating');
    try {
      const genRes = await authFetch(`${API_BASE_URL}/insights/generate?user_id=${userId}&force=true`, {
        method: 'POST',
      });
      const genData = await genRes.json();
      if (genData.success) {
        setInsights({
          reinforcement:      genData.reinforcement,
          recommendations:    genData.recommendations,
          indicators:         genData.indicators,
          last_workout:       genData.last_workout,
          trend_insight_card: genData.trend_insight_card || null,
          generated_at:       new Date().toISOString(),
        });
        setDismissed([]);
        setLoadState('ready');
      } else if (genData.skipped) {
        // Backend skipped (no new activity) — keep existing insights
        setLoadState(insights ? 'ready' : 'no_activities');
      } else {
        setLoadState(insights ? 'ready' : 'error');
      }
    } catch {
      setLoadState(insights ? 'ready' : 'error');
    }
  };

  const visibleRecs = (insights?.recommendations || []).filter(r => !dismissed.includes(r.type));

  return (
    <div className="min-h-screen bg-[#0f172a] text-white" dir="rtl">
      <div className="max-w-lg mx-auto px-4 pb-8">

        {/* Header */}
        <div className="flex items-center justify-between pt-4 pb-3">
          <div>
            <h2 className="text-lg font-bold text-white">תובנות מאמן AI</h2>
            <p className="text-xs text-gray-500">ניתוח אוטומטי של האימונים שלך</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={handleRefresh}
                disabled={loadState === 'generating'}
                className="p-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10 transition-all disabled:opacity-40"
                title="רענן תובנות (אדמין)"
              >
                <svg className={`w-4 h-4 ${loadState === 'generating' ? 'animate-spin' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                </svg>
              </button>
            )}
            {onBack && (
              <button onClick={onBack} className="text-xs text-gray-500 hover:text-white transition-colors px-2">
                ← חזרה
              </button>
            )}
          </div>
        </div>

        {/* States */}
        {loadState === 'loading' && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
            <p className="text-gray-400 text-sm">טוען תובנות...</p>
          </div>
        )}

        {loadState === 'generating' && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="text-3xl animate-pulse">🤖</div>
            <p className="text-gray-300 text-sm font-medium">המאמן מנתח את האימונים שלך...</p>
            <p className="text-gray-500 text-xs">לוקח כ-10 שניות</p>
          </div>
        )}

        {loadState === 'no_activities' && (
          <div className="bg-[#1e293b] border border-white/10 rounded-2xl p-6 text-center">
            <div className="text-4xl mb-3">📊</div>
            <h3 className="text-white font-bold mb-2">עדיין אין אימונים לניתוח</h3>
            <p className="text-gray-400 text-sm">
              התובנות מבוססות על האימונים האמיתיים שלך. חבר את Garmin בפרופיל ← חיבורי אימונים.
            </p>
          </div>
        )}

        {loadState === 'error' && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-5 text-center">
            <p className="text-red-300 text-sm">שגיאה בטעינת התובנות.</p>
            {isAdmin && (
              <button onClick={handleRefresh} className="mt-3 text-xs text-red-400 underline">
                נסה שוב
              </button>
            )}
          </div>
        )}

        {loadState === 'ready' && !insights && (
          <div className="bg-[#1e293b] border border-white/10 rounded-2xl p-6 text-center">
            <div className="text-4xl mb-3">🌅</div>
            <h3 className="text-white font-bold mb-2">אין תובנות עדיין</h3>
            <p className="text-gray-400 text-sm">לחץ על כפתור הרענון לייצר תובנות חדשות.</p>
          </div>
        )}

        {loadState === 'ready' && insights && (
          <AIInsightsCard insights={insights} onDismiss={handleDismiss} />
        )}
      </div>
    </div>
  );
}
